import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'urql';

import {
  Card,
  CardBody,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  NakedForm,
  SubmitButton,
  SwitchField,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { CustomerRestrictionFields } from '@/fragments/CustomerRestriction';
import { useIsMounted } from '@/hooks';
import { Nullable, PlayerId } from '@/types';
import {
  PlayerUpdateRewardsBlockMutation,
  PlayerUpdateRewardsBlockMutationVariables,
} from './__generated__/PlayerUpdateRewardsBlockForm';

const query = graphql`
  query SanityPlayerUpdateRewardsBlockForm {
    sanityPlayerUpdateRewardsBlockForm {
      title {
        ...SanityLocaleString
      }
      description {
        ...SanityLocaleString
      }
      isRewardsBlocked {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const updateCustomerRewardsBlockMutation = gql`
  mutation PlayerUpdateRewardsBlock($playerGlobalId: ID!, $enable: Boolean!) {
    updateCustomerRewardsBlockV2(
      playerGlobalId: $playerGlobalId
      enable: $enable
    ) {
      id
      rewardsBlock {
        ...CustomerRestrictionFields
      }
    }
  }
  ${CustomerRestrictionFields}
`;

const PlayerUpdateRewardsBlockForm: FC<{
  playerGlobalId: PlayerId;
  isBlocked: boolean;
}> = ({ playerGlobalId, isBlocked }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerUpdateRewardsBlockFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();
  const form = staticData.sanityPlayerUpdateRewardsBlockForm;
  const { close } = useModalContext();
  const { t } = useTranslate();

  const [updateRewardsBlockState, updateRewardsBlock] = useMutation<
    PlayerUpdateRewardsBlockMutation,
    PlayerUpdateRewardsBlockMutationVariables
  >(updateCustomerRewardsBlockMutation);

  const defaultValues = {
    isRewardsBlocked: isBlocked,
  };

  const methods = useForm<typeof defaultValues>({ defaultValues });
  const nextValue = methods.watch('isRewardsBlocked');
  const isUnchanged = nextValue === isBlocked;

  const onSubmit = ({ isRewardsBlocked }: typeof defaultValues) => {
    updateRewardsBlock({
      playerGlobalId,
      enable: isRewardsBlocked,
    }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        close();
      }
    });
  };

  if (!form) {
    return null;
  }

  return (
    <Card
      size="md"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <CardBody>
        <div className="p-3">
          <NakedForm
            methods={methods}
            onSubmit={onSubmit}
            className="grid gap-6"
          >
            <div className="space-y-1">
              <div className="block text-gray-600 text-sm dark:text-gray-200">
                {t(form.description)}
              </div>
            </div>
            <SwitchField
              title={t(form.isRewardsBlocked)}
              name="isRewardsBlocked"
              labelPlacement="above"
              className="col-span-full"
              allowUndefinedState={false}
            />
            <ErrorMessage message={errorMessage} />
            <SubmitButton
              value={t(form.submit)}
              disabled={updateRewardsBlockState.fetching || isUnchanged}
            />
          </NakedForm>
        </div>
      </CardBody>
    </Card>
  );
};

export default PlayerUpdateRewardsBlockForm;
